// Instant quality check — client-side, pre-upload, advisory only. No network, no AI.
// Returns a Promise<string[]> of flags among: "blurry", "dark", "glare".
// Silently degrades to [] if canvas is unsupported or anything fails — never blocks capture.

const DOWN_W = 320;
const BLUR_LAPVAR_MAX = 15;   // below → blurry (only checked when not dark/glare)
const DARK_MEAN_MAX = 45;     // mean luminance below this + mostly-dark → dark
const DARK_FRAC_MIN = 0.6;    // fraction of pixels with lum < 30
const GLARE_WHITE_FRAC = 0.5; // fraction of pixels with lum > 245 → blown out
const GLARE_MEAN_MAX = 248;   // or overall extremely bright

const COPY = {
  blurry: "This one looks blurry — retake?",
  dark: "Very dark — turn on lights?",
  glare: "Bright glare — try another angle?",
};

export function qualityCopy(flags) {
  if (!flags || !flags.length) return "";
  return COPY[flags[0]] || "";
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ img, url }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

export async function checkImageQuality(file) {
  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return [];
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return []; // unsupported → degrade

    const { img, url } = await loadImage(file);
    try {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return [];
      const dw = Math.min(DOWN_W, w);
      const dh = Math.max(1, Math.round((h * dw) / w));
      canvas.width = dw; canvas.height = dh;
      ctx.drawImage(img, 0, 0, dw, dh);

      let data;
      try { data = ctx.getImageData(0, 0, dw, dh).data; }
      catch { return []; } // tainted/unreadable → degrade

      // grayscale luminance
      const n = dw * dh;
      const lum = new Float32Array(n);
      let sum = 0, darkCount = 0, whiteCount = 0;
      for (let i = 0, p = 0; i < n; i++, p += 4) {
        const l = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
        lum[i] = l; sum += l;
        if (l < 30) darkCount++;
        if (l > 245) whiteCount++;
      }
      const mean = sum / n;
      const darkFrac = darkCount / n;
      const whiteFrac = whiteCount / n;

      const flags = [];
      const isDark = mean < DARK_MEAN_MAX && darkFrac > DARK_FRAC_MIN;
      const isGlare = whiteFrac > GLARE_WHITE_FRAC || mean > GLARE_MEAN_MAX;
      if (isDark) flags.push("dark");
      if (isGlare) flags.push("glare");

      // Blur (Laplacian variance) — skip when already dark/glare (those depress variance too).
      if (!isDark && !isGlare) {
        let ls = 0, ls2 = 0, cnt = 0;
        for (let y = 1; y < dh - 1; y++) {
          for (let x = 1; x < dw - 1; x++) {
            const idx = y * dw + x;
            const v = -4 * lum[idx] + lum[idx - 1] + lum[idx + 1] + lum[idx - dw] + lum[idx + dw];
            ls += v; ls2 += v * v; cnt++;
          }
        }
        if (cnt > 0) {
          const m = ls / cnt;
          const variance = ls2 / cnt - m * m;
          if (variance < BLUR_LAPVAR_MAX) flags.push("blurry");
        }
      }
      return flags;
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return []; // any failure → no warning, upload proceeds normally
  }
}
